import { useEffect, useMemo, useState } from 'react'
import cn from 'classnames'
import { Button, MultiSelect, Select, TextInput } from '@forwardflow/ui-v2'
import { Chip } from '@forwardflow/features/campaign-v2/components/glimpse'
import { AudienceEstimate } from '../AudienceEstimate'
import {
   useBusinessCategories,
   useEstimatedLeads,
} from '../../queries/campaign-v2.queries'
import { useAudienceVocabulary } from '../../hooks/useAudienceVocabulary'
import { TagPicker } from '../shared/TagPicker'
import { useCampaignV2Store } from '../../store/editor.store'
import {
   ICriteria,
   TCriteriaOperator,
} from '../../interfaces/campaign-v2.types'
import {
   CRITERIA_FIELDS,
   criteriaField,
   criteriaPhrase,
   criteriaValues,
   syncPools,
} from '../../utils/config'
import {
   AddRow,
   CheckBoxDot,
   EmptyNote,
   Field,
   IStepFooter,
   ListCard,
   RadioDot,
   StepFrame,
   TextButton,
   Tile,
} from '../shared'

/**
 * Step 1 — who we call. The campaign's name, its business categories, and
 * either "leads that match these filters" (dynamic source) or "leads I add
 * myself" (manual). Filters read as a sentence, like the setup composer.
 */
export const AudienceStep = ({ footer }: { footer: IStepFooter }) => {
   const {
      name,
      campaignId,
      revision,
      setName,
      config,
      setConfig,
      setSourceMode,
      categoryLabels,
      setCategoryLabels,
      audienceLabels,
      addAudienceLabels,
   } = useCampaignV2Store()
   const { data: categories, isLoading: categoriesLoading } =
      useBusinessCategories()
   const vocab = useAudienceVocabulary()
   const [adding, setAdding] = useState(false)

   useEffect(() => {
      if (categories?.length)
         setCategoryLabels(
            Object.fromEntries(categories.map((c) => [c.id, c.name]))
         )
   }, [categories, setCategoryLabels])

   // Pond ids are numbers; the sentence should say the pond's name.
   useEffect(() => {
      if (vocab.ponds.length)
         addAudienceLabels(
            Object.fromEntries(
               vocab.ponds.map((p) => [`assigned_pond_id:${p.value}`, p.label])
            )
         )
   }, [vocab.ponds, addAudienceLabels])

   const criteria =
      config?.source.mode === 'dynamic' ? config.source.criteria : []
   const { data: estimate, isValidating: estimating, error: estimateError, mutate: retryEstimate } = useEstimatedLeads(
      config?.source.mode === 'dynamic' ? criteria : null,
      campaignId,
      revision
   )

   const toggleCategory = (id: number) => {
      if (!config) return
      const next = config.businessTypeIds.includes(id)
         ? config.businessTypeIds.filter((x) => x !== id)
         : [...config.businessTypeIds, id]
      if (!next.length) return
      if (
         config.businessTypeIds.includes(id) &&
         !window.confirm(
            'Remove this business type and its content selections from the draft?'
         )
      )
         return
      setConfig((c) => syncPools(c, next, categoryLabels))
   }

   const setCriteria = (next: ICriteria[]) =>
      setConfig((c) => ({ ...c, source: { mode: 'dynamic', criteria: next } }))

   const rulesFor = (businessTypeId: number) =>
      (config?.businessTypeRules || []).find(
         (r) => r.businessTypeId === businessTypeId
      )?.conditions?.[0]
   const setRuleTags = (businessTypeId: number, tags: string[]) =>
      setConfig((c) => {
         const rest = (c.businessTypeRules || []).filter(
            (r) => r.businessTypeId !== businessTypeId
         )
         const rule = tags.length
            ? [
                 {
                    businessTypeId,
                    conditions: [
                       {
                          field: 'tags',
                          operator: 'CONTAINS_ANY' as const,
                          value: tags,
                       },
                    ],
                 },
              ]
            : []
         return { ...c, businessTypeRules: [...rest, ...rule] }
      })

   const mixed = (config?.businessTypeIds.length || 0) > 1
   const rulesComplete =
      !mixed ||
      config!.businessTypeIds.every(
         (id) =>
            (rulesFor(id)?.value as string[] | undefined)?.length ||
            config!.contentPools
               .filter((p) => p.businessTypeId === id)
               .every((p) =>
                  config!.subtypes.some(
                     (s) => s.poolId === p.id && s.description.trim()
                  )
               )
      )

   const canContinue =
      Boolean(name.trim()) &&
      Boolean(config?.businessTypeIds.length) &&
      rulesComplete
   const why = !name.trim()
      ? 'Give the campaign a name'
      : !config?.businessTypeIds.length
        ? 'Pick at least one category'
        : !rulesComplete
          ? 'Add a description for every lead type under Content'
          : undefined

   if (!config) return null

   return (
      <StepFrame
         eyebrow="Step 1 of 8 · Audience"
         title="Who should this campaign work?"
         lede="Name it, say what kind of leads these are, and choose whether we find them from Follow Up Boss filters or you add them yourself."
         footer={{
            ...footer,
            disabled: footer.disabled || !canContinue,
            action: why || footer.action,
         }}
         testId="step-audience"
      >
         <TextInput
            size="sm"
            label="Campaign name"
            placeholder="Zillow buyers, March"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            maxLength={120}
            data-testid="campaign-name"
         />

         <Field
            label="What kind of leads"
            hint="Each category gets its own scripts and messages. Lead type descriptions and any existing rules determine which content each lead receives."
         >
            <div
               className="flex flex-wrap gap-2"
               role="group"
               data-testid="categories"
            >
               {categoriesLoading && !categories?.length && (
                  <span className="text-white/40 text-sm">
                     Loading categories…
                  </span>
               )}
               {(categories || []).map((c) => {
                  const on = config.businessTypeIds.includes(c.id)
                  return (
                     <button
                        key={c.id}
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        onClick={() => toggleCategory(c.id)}
                        data-testid={`category-${c.id}`}
                        className={cn(
                           'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
                           on
                              ? 'border-[#9F7BFF] bg-[#9F7BFF]/10 text-white'
                              : 'border-white/10 bg-white/[0.02] text-white/70 hover:border-white/25'
                        )}
                     >
                        <CheckBoxDot on={on} /> {c.name}
                     </button>
                  )
               })}
            </div>
         </Field>

         {mixed && !!config.businessTypeRules?.length && (
            <ListCard
               title="How do we tell them apart?"
               hint="Existing category rules. Lead type descriptions are managed under Content."
               testId="category-rules"
            >
               {config.businessTypeIds.map((id) => (
                  <div key={id} className="flex items-center gap-3 px-4 py-2.5">
                     <span className="w-[110px] shrink-0 text-sm text-white/80">
                        {categoryLabels[id] || `Category ${id}`}
                     </span>
                     <span className="text-xs text-white/45 shrink-0">
                        tagged
                     </span>
                     {config.businessTypeRules
                        ?.find((r) => r.businessTypeId === id)
                        ?.conditions.some(
                           (c) =>
                              c.field !== 'tags' ||
                              c.operator !== 'CONTAINS_ANY'
                        ) ||
                     (config.businessTypeRules?.find(
                        (r) => r.businessTypeId === id
                     )?.conditions.length || 0) > 1 ? (
                        <span className="text-xs text-white/60">
                           Existing advanced rules are preserved.
                        </span>
                     ) : (
                        <TagPicker
                           className="flex-1"
                           baseOptions={vocab.tags}
                           value={(rulesFor(id)?.value as string[]) || []}
                           onChange={(v) => setRuleTags(id, v)}
                           placeholder="Choose tags"
                           testId={`rule-${id}`}
                        />
                     )}
                  </div>
               ))}
            </ListCard>
         )}

         <Field label="Where the leads come from">
            <div
               className="grid grid-cols-1 sm:grid-cols-2 gap-2.5"
               role="radiogroup"
            >
               <Tile
                  selected={config.source.mode === 'dynamic'}
                  onClick={() => setSourceMode('dynamic')}
                  testId="mode-dynamic"
               >
                  <div className="flex items-start gap-3">
                     <RadioDot on={config.source.mode === 'dynamic'} />
                     <div>
                        <p className="text-white text-sm font-medium">
                           Leads that match filters
                        </p>
                        <p className="text-white/50 text-xs mt-0.5">
                           We keep finding matching leads in Follow Up Boss
                           after the campaign goes live.
                        </p>
                     </div>
                  </div>
               </Tile>
               <Tile
                  selected={config.source.mode === 'manual'}
                  onClick={() => setSourceMode('manual')}
                  testId="mode-manual"
               >
                  <div className="flex items-start gap-3">
                     <RadioDot on={config.source.mode === 'manual'} />
                     <div>
                        <p className="text-white text-sm font-medium">
                           Leads I add myself
                        </p>
                        <p className="text-white/50 text-xs mt-0.5">
                           Nothing happens until you enroll leads. Good for a
                           hand-picked list.
                        </p>
                     </div>
                  </div>
               </Tile>
            </div>
         </Field>

         {config.source.mode === 'dynamic' && (
            <ListCard
               title={
                  <span data-testid="audience-sentence">
                     {criteria.length
                        ? 'Leads ' +
                          criteria
                             .map((c) => criteriaPhrase(c, audienceLabels))
                             .join(', ')
                        : 'All leads in Follow Up Boss'}
                  </span>
               }
               testId="criteria-card"
            >
               <AudienceEstimate
                  estimate={estimate}
                  loading={estimating}
                  failed={Boolean(estimateError)}
                  campaignId={campaignId}
                  onRetry={() => { void retryEstimate() }}
               />
               {criteria.map((c, i) => (
                  <CriteriaRow
                     key={`${c.field}-${i}`}
                     c={c}
                     labels={audienceLabels}
                     onRemove={() =>
                        setCriteria(criteria.filter((_, j) => j !== i))
                     }
                     testId={`criteria-${i}`}
                  />
               ))}
               {adding ? (
                  <AddCriteria
                     vocab={vocab}
                     onAdd={(c) => {
                        setCriteria([...criteria, c])
                        setAdding(false)
                     }}
                     onCancel={() => setAdding(false)}
                  />
               ) : (
                  <AddRow
                     label="Add a filter"
                     onClick={() => setAdding(true)}
                     testId="add-criteria"
                  />
               )}
            </ListCard>
         )}

         {config.source.mode === 'manual' && (
            <ListCard testId="manual-note">
               <EmptyNote>
                  You will enroll leads from the campaign page once it is live.
                  Leads on your stop list are skipped automatically.
               </EmptyNote>
            </ListCard>
         )}
      </StepFrame>
   )
}

const CriteriaRow = ({
   c,
   labels,
   onRemove,
   testId,
}: {
   c: ICriteria
   labels: Record<string, string>
   onRemove: () => void
   testId: string
}) => {
   const field = criteriaField(c.field)
   const op = field?.operators.find((o) => o.id === c.operator)
   const values = criteriaValues(c).map((v) => labels[`${c.field}:${v}`] || v)
   return (
      <div
         className="flex items-center gap-3 px-4 min-h-[48px] py-1.5"
         data-testid={testId}
      >
         <span className="w-[110px] shrink-0 text-[13px] font-medium text-white/80">
            {field?.label || c.field}
         </span>
         <span className="text-[13px] text-white/45 shrink-0">
            {op?.label || c.operator}
         </span>
         <span className="flex-1 min-w-0 flex flex-wrap gap-1.5">
            {values.map((v) => (
               <Chip
                  key={v}
                  kind={
                     field?.picker === 'tags'
                        ? 'tag'
                        : field?.picker === 'stage'
                          ? 'stage'
                          : undefined
                  }
               >
                  {v}
               </Chip>
            ))}
         </span>
         <TextButton
            onClick={onRemove}
            tone="muted"
            testId={`${testId}-remove`}
         >
            Remove
         </TextButton>
      </div>
   )
}

const AddCriteria = ({
   vocab,
   onAdd,
   onCancel,
}: {
   vocab: ReturnType<typeof useAudienceVocabulary>
   onAdd: (c: ICriteria) => void
   onCancel: () => void
}) => {
   const [fieldId, setFieldId] = useState(CRITERIA_FIELDS[0].id)
   const field = criteriaField(fieldId)!
   const [operator, setOperator] = useState<TCriteriaOperator>(
      field.operators[0].id
   )
   const [list, setList] = useState<string[]>([])
   const [text, setText] = useState('')

   const options = useMemo(() => {
      switch (field.picker) {
         case 'source':
            return vocab.sources
         case 'stage':
            return vocab.stages
         case 'tags':
            return vocab.tags
         case 'pond':
            return vocab.ponds
         default:
            return []
      }
   }, [field.picker, vocab])

   const isList = ['source', 'stage', 'tags', 'pond'].includes(field.picker)
   const ready = isList ? list.length > 0 : text.trim().length > 0

   const submit = () => {
      if (!ready) return
      const value: ICriteria['value'] = isList
         ? field.picker === 'pond'
            ? list.map(Number)
            : list
         : field.picker === 'number'
           ? Number(text)
           : text.trim()
      onAdd({ field: field.id, operator, value, logic: 'AND' })
   }

   return (
      <div
         className="px-4 py-3 bg-[#9F7BFF]/[0.05] space-y-2"
         data-testid="add-criteria-row"
      >
         <div className="grid grid-cols-2 gap-2">
            <Select
               size="sm"
               data={CRITERIA_FIELDS.map((f) => ({
                  value: f.id,
                  label: f.label,
               }))}
               value={fieldId}
               onChange={(v) => {
                  const next = criteriaField(v || CRITERIA_FIELDS[0].id)!
                  setFieldId(next.id)
                  setOperator(next.operators[0].id)
                  setList([])
                  setText('')
               }}
               data-testid="criteria-field"
            />
            <Select
               size="sm"
               data={field.operators.map((o) => ({
                  value: o.id,
                  label: o.label,
               }))}
               value={operator}
               onChange={(v) =>
                  setOperator((v as TCriteriaOperator) || field.operators[0].id)
               }
               data-testid="criteria-operator"
            />
         </div>
         <div className="flex items-center gap-2">
            {isList ? (
               field.picker === 'tags' ? (
                  <TagPicker
                     className="flex-1"
                     baseOptions={vocab.tags}
                     value={list}
                     onChange={setList}
                     placeholder={
                        options.length
                           ? 'Choose values'
                           : vocab.loading
                             ? 'Loading…'
                             : 'Nothing to choose from yet'
                     }
                     testId="criteria-values"
                  />
               ) : (
                  <MultiSelect
                     size="sm"
                     className="flex-1"
                     data={options}
                     value={list}
                     onChange={setList}
                     placeholder={
                        options.length
                           ? 'Choose values'
                           : vocab.loading
                             ? 'Loading…'
                             : 'Nothing to choose from yet'
                     }
                     searchable
                     data-testid="criteria-values"
                  />
               )
            ) : (
               <TextInput
                  size="sm"
                  className="flex-1"
                  type={
                     field.picker === 'date'
                        ? 'date'
                        : field.picker === 'number'
                          ? 'number'
                          : 'text'
                  }
                  value={text}
                  onChange={(e) => setText(e.currentTarget.value)}
                  placeholder={field.picker === 'number' ? '250000' : undefined}
                  data-testid="criteria-text"
               />
            )}
            <Button
               size="middle"
               className="!h-9"
               disabled={!ready}
               onClick={submit}
               data-testid="criteria-add"
            >
               Add
            </Button>
            <Button
               size="middle"
               variant="secondary"
               className="!h-9"
               onClick={onCancel}
            >
               Cancel
            </Button>
         </div>
      </div>
   )
}
